// FootyStats enrichment adapter (frontend seam).
// FootyStats is an EVIDENCE/CONTEXT provider for Prematch & Match Detail — it NEVER
// replaces ATLAS decisions. All calls go through the backend bridge so the API key
// stays server-side. When FOOTYSTATS_API_KEY is not configured, the bridge returns
// { configured: false } and every enrichment resolves to an honest unavailable state.
//
// The adapter is intentionally provider-agnostic: swapping FootyStats for another
// licensed provider later only requires changing the backend, not this contract.

import { apiRequest } from "@/api/client";

export interface EnrichmentField<T = number> {
  available: boolean;
  value: T | null;
}

export interface TeamStatProfile {
  available: boolean;
  formLast5: string[] | null;       // e.g. ["W","D","L","W","W"]
  goalsForAvg: number | null;
  goalsAgainstAvg: number | null;
  homeAwaySplit: { home: number | null; away: number | null } | null;
  xgFor: number | null;
  xgAgainst: number | null;
  bttsPct: number | null;
  over25Pct: number | null;
  cornersAvg: number | null;
}

export interface MatchEnrichment {
  configured: boolean;
  home: TeamStatProfile | null;
  away: TeamStatProfile | null;
  h2h: { available: boolean; matches: Array<{ date: string; score: string; label: string }> | null } | null;
  leagueContext: { available: boolean; standings: Array<{ team: string; position: number; points: number }> | null } | null;
  source: string | null;            // provider name for attribution when present
  updatedAt: string | null;
}

const EMPTY: MatchEnrichment = {
  configured: false, home: null, away: null, h2h: null, leagueContext: null, source: null, updatedAt: null,
};

export async function isEnrichmentConfigured(): Promise<boolean> {
  try {
    const r = await apiRequest<{ configured: boolean }>(`/enrich/status`, { retries: 0 });
    return !!r.configured;
  } catch {
    return false;
  }
}

// Fetch enrichment for a fixture. Never throws to the UI — resolves to EMPTY on any gap.
export async function fetchMatchEnrichment(fixtureId: string): Promise<MatchEnrichment> {
  try {
    const r = await apiRequest<MatchEnrichment>(`/enrich/match/${encodeURIComponent(fixtureId)}`, { retries: 0 });
    return { ...EMPTY, ...r };
  } catch {
    return EMPTY;
  }
}
